import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import DashboardLayout from '@/layouts/DashboardLayout';
import RequirePermission from './RequirePermission';
import { PERMISSIONS } from '@/utils/permissions';

const Login = lazy(() => import('@/pages/Login/Login'));
const Dashboard = lazy(() => import('@/pages/Dashboard/Dashboard'));
const Activity = lazy(() => import('@/pages/Activity/Activity'));
const Groups = lazy(() => import('@/pages/Groups/Groups'));
const GroupDetail = lazy(() => import('@/pages/Groups/GroupDetail'));
const Users = lazy(() => import('@/pages/Users/Users'));
const UserDetail = lazy(() => import('@/pages/Users/UserDetail'));
const Organizations = lazy(() => import('@/pages/Organizations/Organizations'));
const OrganizationDetail = lazy(() => import('@/pages/Organizations/OrganizationDetail'));
const OrganizationGroupDetail = lazy(
  () => import('@/pages/Organizations/OrganizationGroupDetail')
);
const Wave = lazy(() => import('@/pages/Wave/Wave'));
const WaveReports = lazy(() => import('@/pages/Wave/WaveReports'));
const Goals = lazy(() => import('@/pages/Goals/Goals'));
const Revenue = lazy(() => import('@/pages/Revenue/Revenue'));
const EmailTemplates = lazy(() => import('@/pages/Notifications/EmailTemplates'));
const AllNotifications = lazy(() => import('@/pages/Notifications/AllNotifications'));
const EmailLogs = lazy(() => import('@/pages/Notifications/EmailLogs'));
const Scheduler = lazy(() => import('@/pages/Notifications/Scheduler'));
const WeeklyReport = lazy(() => import('@/pages/Notifications/WeeklyReport'));
const Settings = lazy(() => import('@/pages/Settings/Settings'));
const Permissions = lazy(() => import('@/pages/Settings/Permissions'));
const ReportLog = lazy(() => import('@/pages/Settings/ReportLog'));
const NotFound = lazy(() => import('@/pages/NotFound/NotFound'));

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#fff7ed]">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-orange-200 border-t-orange-500" />
    </div>
  );
}

export default function Navigation() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route
              index
              element={
                <RequirePermission anyOf={[PERMISSIONS.DASHBOARD_VIEW]}>
                  <Dashboard />
                </RequirePermission>
              }
            />
            <Route
              path="activity"
              element={
                <RequirePermission anyOf={[PERMISSIONS.ACTIVITIES_VIEW]}>
                  <Activity />
                </RequirePermission>
              }
            />
            <Route
              path="groups"
              element={
                <RequirePermission anyOf={[PERMISSIONS.GROUPS_VIEW]}>
                  <Groups />
                </RequirePermission>
              }
            />
            <Route
              path="groups/:id"
              element={
                <RequirePermission anyOf={[PERMISSIONS.GROUPS_VIEW]}>
                  <GroupDetail />
                </RequirePermission>
              }
            />
            <Route
              path="users"
              element={
                <RequirePermission anyOf={[PERMISSIONS.USERS_VIEW]}>
                  <Users />
                </RequirePermission>
              }
            />
            <Route
              path="users/:id"
              element={
                <RequirePermission anyOf={[PERMISSIONS.USERS_VIEW]}>
                  <UserDetail />
                </RequirePermission>
              }
            />
            <Route
              path="organizations"
              element={
                <RequirePermission orgManager>
                  <Organizations />
                </RequirePermission>
              }
            />
            <Route
              path="organizations/:id"
              element={
                <RequirePermission orgManager>
                  <OrganizationDetail />
                </RequirePermission>
              }
            />
            <Route
              path="organizations/:id/groups/:groupId"
              element={
                <RequirePermission orgManager>
                  <OrganizationGroupDetail />
                </RequirePermission>
              }
            />
            <Route
              path="wave"
              element={
                <RequirePermission anyOf={[PERMISSIONS.WAVES_VIEW]}>
                  <Wave />
                </RequirePermission>
              }
            />
            <Route
              path="wave/reports"
              element={
                <RequirePermission anyOf={[PERMISSIONS.WAVES_VIEW]}>
                  <WaveReports />
                </RequirePermission>
              }
            />
            <Route path="goals" element={<Goals />} />
            <Route path="revenue" element={<Revenue />} />
            <Route
              path="notifications/email-templates"
              element={
                <RequirePermission anyOf={[PERMISSIONS.TEMPLATES_VIEW]}>
                  <EmailTemplates />
                </RequirePermission>
              }
            />
            <Route
              path="notifications/all"
              element={
                <RequirePermission anyOf={[PERMISSIONS.NOTIFICATIONS_VIEW]}>
                  <AllNotifications />
                </RequirePermission>
              }
            />
            <Route
              path="notifications/email-logs"
              element={
                <RequirePermission anyOf={[PERMISSIONS.NOTIFICATIONS_VIEW]}>
                  <EmailLogs />
                </RequirePermission>
              }
            />
            <Route
              path="notifications/scheduler"
              element={
                <RequirePermission
                  anyOf={[PERMISSIONS.SCHEDULER_VIEW, PERMISSIONS.SCHEDULED_JOBS_VIEW]}
                >
                  <Scheduler />
                </RequirePermission>
              }
            />
            <Route
              path="notifications/weekly-report"
              element={
                <RequirePermission anyOf={[PERMISSIONS.NOTIFICATIONS_VIEW]}>
                  <WeeklyReport />
                </RequirePermission>
              }
            />
            <Route path="settings" element={<Settings />} />
            <Route
              path="settings/permissions"
              element={
                <RequirePermission anyOf={[PERMISSIONS.ROLES_VIEW]}>
                  <Permissions />
                </RequirePermission>
              }
            />
            <Route
              path="settings/report-log"
              element={
                <RequirePermission anyOf={[PERMISSIONS.REPORTS_VIEW]}>
                  <ReportLog />
                </RequirePermission>
              }
            />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
